import React, { useCallback, useEffect, useState } from "react";
import DSDialog from "../../system-design/DSDialog";
import CollectionForm from "./CollectionForm";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import useCollection from "../hooks/useCollection";
import DSButton from "../../system-design/DSButton";
import DSBox from "../../system-design/DSBox";
import useRemoveCollectionMutation from "../hooks/useRemoveCollectionMutation";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSTypography from "../../system-design/DSTypography";
import DSChip from "../../system-design/DSChip";
import DSTooltip from "../../system-design/DSTooltip";
import useCollectionShareMutation from "../hooks/useCollectionShareMutation";
import useWebQualityImageDownload from "../hooks/useWebQualityImageDownload";
import useDownloadCollectionFrames from "../hooks/useDownloadCollectionFrames";
import usePublishCollectionToShopifyMutation from "../hooks/usePublishCollectionToShopifyMutation";
import useEditionMutation from "../hooks/useEditionMutation";
import { isAxiosError } from "axios";
import {
  getPreviewReadiness,
  useShopifyPreviewUrlQuery,
} from "../hooks/useShopifyPreviewUrlQuery";
import {
  CollectionType,
  DateValue,
  EditApprovedCollectionInput,
  MarketplaceStatus,
} from "../../../api/collection";
import useEditApprovedCollectionMutation from "../hooks/useEditApprovedCollectionMutation";
import { usePlansQuery } from "../../Studio/hooks/usePlans";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";
import useUpdateCollectionPlanMutation from "../hooks/useUpdateCollectionPlanMutation";
import useSetSponsoredCollectionMutation from "../hooks/useSetSponsoredCollectionMutation";
import useSponsoredCollectionCode from "../hooks/useSponsoredCollectionCode";
import SponsoredCodeEditor from "./SponsoredCodeEditor";
import DSSwitch from "../../system-design/DSSwitch";
import DateTimePicker from "../../system-design/DSDatePicker";
import { Autocomplete, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ConfirmationModal from "../../ConfirmationModal";
import { ClocksListResponse, MachineCollection } from "../../../Types";
import adminApiClient from "../../../api/admin";
import firebaseService from "../../../firebase_service/firebaseService";
import useClocksInfiniteQuery from "../../Device/hooks/useClocksInfiniteQuery";
import useListboxInfiniteScroll, {
  noClientFilter,
} from "../../Device/hooks/useListboxInfiniteScroll";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import {
  isAwaitingScheduledPublish,
  scheduledMarketplaceLabel,
} from "../utils/marketplaceSchedule";

const CollectionDialog = ({
  collectionId,
  onClose,
  ...rest
}: { collectionId: string; onClose: () => void } & Omit<
  React.ComponentProps<typeof DSDialog>,
  "onClose"
>) => {
  const { mutateAsync: deleteCollection } = useRemoveCollectionMutation();
  const { data: collection, isLoading } = useCollection(collectionId);
  const {
    mutate: shareCollection,
    isPending: shareLoading,
    isSuccess: shareSuccess,
  } = useCollectionShareMutation();
  const { downloadImage, isDownloading } = useWebQualityImageDownload();
  const { downloadFrames, isDownloading: isDownloadingFrames } =
    useDownloadCollectionFrames();
  const { mutate: publishToShopify, isPending: isPublishing } =
    usePublishCollectionToShopifyMutation();
  const { mutateAsync: requestEdition, isPending: isEditionPending } =
    useEditionMutation();
  const [isEditionModalOpen, setIsEditionModalOpen] = useState(false);
  const [editionUrl, setEditionUrl] = useState("");
  const [editionCopied, setEditionCopied] = useState(false);
  const [editionError, setEditionError] = useState("");
  const { data: cachedPreviewData, isFetching: isLoadingPreview } =
    useShopifyPreviewUrlQuery(collectionId, collection);
  const [name, setName] = useState(collection?.name || "");
  const [description, setDescription] = useState(collection?.description || "");
  const [coverUrl, setCoverUrl] = useState(collection?.coverUrl || "");
  const [logoUrl, setLogoUrl] = useState(collection?.logoUrl || "");
  const [logoTypeUrl, setLogoTypeUrl] = useState(
    collection?.productPageMetadata?.logoTypeUrl || "",
  );
  const [thumbnailBackgroundUrl, setThumbnailBackgroundUrl] = useState(
    collection?.productPageMetadata?.thumbnailBackgroundUrl || "",
  );
  const [splashBackgroundUrl, setSplashBackgroundUrl] = useState(
    collection?.splashBackgroundUrl || "",
  );
  const [menuLogoUrl, setMenuLogoUrl] = useState(collection?.menuLogoUrl || "");
  const [badgeColorHex, setBadgeColorHex] = useState(
    collection?.productPageMetadata?.badgeColorHex || "#000000",
  );
  const [klydosIds, setKlydosIds] = useState<string[]>([]);
  const [holdPendulumLogo, setHoldPendulumLogo] = useState(
    collection?.holdPendulumLogo || false,
  );
  const [labels, setLabels] = useState<string[]>(
    collection?.productPageMetadata?.labels || [],
  );
  const [planId, setPlanId] = useState<string | undefined>(
    collection?.paymentDetails?.planId,
  );
  const { data: plans } = usePlansQuery();
  const availablePlans = plans ?? [];
  const [planError, setPlanError] = useState<string>("");
  const { mutate: updatePlan, isPending: isUpdatingPlan } =
    useUpdateCollectionPlanMutation();
  const { mutate: setSponsored, isPending: isUpdatingSponsored } =
    useSetSponsoredCollectionMutation();
  const isApproved = collection?.type === CollectionType.APPROVED;
  const { data: sponsoredCodeInfo } = useSponsoredCollectionCode(
    collectionId,
    isApproved,
  );
  // The cover uploads to Cloudinary inside CollectionForm; block "Save Details"
  // until it lands so we never save the old url and report success.
  const [isCoverUploading, setIsCoverUploading] = useState(false);
  const [editError, setEditError] = useState<string>("");
  const [editSaved, setEditSaved] = useState(false);
  const { mutate: editCollection, isPending: isSavingEdit } =
    useEditApprovedCollectionMutation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [publishImmediate, setPublishImmediate] = useState(true);
  const [publishDate, setPublishDate] = useState<Date>(new Date());
  const [userCreatorId, setUserCreatorId] = useState<string>(
    collection?.creatorId || "",
  );

  // "Add to Clock": pushes the collection into the target machine's
  // `machineCollections` config. Works for drafts and approved alike; the
  // API/device impose no draft-vs-approved gating, so the only guard we add is
  // the minimum-content requirement for drafts (see below).
  const [showAddToClockSection, setShowAddToClockSection] = useState(false);
  const [clockSearchQuery, setClockSearchQuery] = useState("");
  const [selectedClock, setSelectedClock] = useState<ClocksListResponse | null>(
    null,
  );
  const [showAddToClockConfirmation, setShowAddToClockConfirmation] =
    useState(false);

  const {
    data: availableClocks,
    isLoading: isLoadingClocks,
    fetchNextPage: fetchNextClocks,
    hasNextPage: hasNextClocks,
    isFetchingNextPage: isFetchingNextClocks,
  } = useClocksInfiniteQuery({ query: clockSearchQuery });

  const { listboxProps } = useListboxInfiniteScroll({
    fetchNextPage: fetchNextClocks,
    hasNextPage: hasNextClocks,
    isFetchingNextPage: isFetchingNextClocks,
  });

  const handleConfirmAddToClock = useCallback(async () => {
    if (!selectedClock) return;
    try {
      // Read the clock's current collections so we append rather than clobber.
      const clock = await adminApiClient.getClock(selectedClock.id);
      const existingCollections: MachineCollection[] =
        clock?.machineCollections?.value || [];

      if (existingCollections.some((c) => c.id === collectionId)) {
        alert("This collection is already added to the selected clock.");
        setShowAddToClockConfirmation(false);
        setSelectedClock(null);
        return;
      }

      const updatedCollections: MachineCollection[] = [
        ...existingCollections,
        { id: collectionId, permanent: false },
      ];

      await firebaseService.updateConfig(
        selectedClock.id,
        "machineCollections",
        updatedCollections,
      );

      alert(
        `Collection successfully added to clock ${selectedClock.idf || selectedClock.id}`,
      );
      setShowAddToClockConfirmation(false);
      setSelectedClock(null);
    } catch (error) {
      console.error("Error adding collection to clock:", error);
      alert("Failed to add collection to clock. Please try again.");
    }
  }, [selectedClock, collectionId]);

  const handleCancelAddToClock = useCallback(() => {
    setShowAddToClockConfirmation(false);
    setSelectedClock(null);
  }, []);

  const handleShareClick = useCallback(() => {
    shareCollection(collectionId, {
      onSuccess: async (shareUrl: string) => {
        await navigator.clipboard.writeText(shareUrl);
      },
    });
  }, [shareCollection, collectionId]);

  const handleDownloadCover = useCallback(() => {
    if (coverUrl) {
      const filename = `${name || "collection"}-cover-web`;
      downloadImage(coverUrl, filename);
    }
  }, [downloadImage, coverUrl, name]);

  const handleDownloadFrames = useCallback(() => {
    if (collection?.klydos?.length) {
      downloadFrames(collection.klydos, name || "collection");
    }
  }, [downloadFrames, collection?.klydos, name]);

  const handlePublishToShopify = useCallback(() => {
    setPublishImmediate(true);
    setPublishDate(new Date());
    setIsPublishModalOpen(true);
  }, []);

  const handleConfirmCreateEdition = useCallback(async () => {
    setIsEditionModalOpen(false);
    setEditionError("");
    try {
      const { url } = await requestEdition({
        collectionId,
        creator: "backoffice",
      });
      setEditionUrl(url);
    } catch (error) {
      setEditionError(
        (isAxiosError(error) && error.response?.data?.message) ||
          "Failed to create the edition. Please try again.",
      );
    }
  }, [requestEdition, collectionId]);

  // The url is not stored on the collection (only the bundle product id), so
  // the Copy button re-asks the API. Sending the collection's OWN creator kind
  // makes the endpoint a pure read: it returns the existing bundle's url
  // without creating anything. Absent kind means a pre-migration artist
  // edition.
  const editionCreator =
    collection?.productPageMetadata?.affiliateBundleCreator ?? "artist";
  const handleCopyEditionUrl = useCallback(async () => {
    setEditionError("");
    try {
      const url =
        editionUrl ||
        (await requestEdition({ collectionId, creator: editionCreator })).url;
      setEditionUrl(url);
      await navigator.clipboard.writeText(url);
      setEditionCopied(true);
      setTimeout(() => setEditionCopied(false), 3000);
    } catch (error) {
      setEditionError(
        (isAxiosError(error) && error.response?.data?.message) ||
          "Failed to fetch the edition URL. Please try again.",
      );
    }
  }, [requestEdition, collectionId, editionCreator, editionUrl]);

  const handleConfirmPublish = useCallback(() => {
    setIsPublishModalOpen(false);
    let date: DateValue | undefined;
    if (!publishImmediate) {
      date = {
        year: publishDate.getFullYear(),
        month: publishDate.getMonth() + 1,
        day: publishDate.getDate(),
      };
    }
    publishToShopify({ collectionId, date });
  }, [publishToShopify, collectionId, publishImmediate, publishDate]);

  const isPublished =
    collection?.marketplace?.status === MarketplaceStatus.True;

  const isDraft = !isApproved;

  // Drafts may only be sent to a clock once they carry enough content to render
  // meaningfully. Approved collections have already cleared review, so the gate
  // applies to drafts only. All fields below live on the populated collection —
  // no extra fetch needed.
  const missingRequirements: string[] = [];
  if (!collection?.coverUrl) missingRequirements.push("Cover");
  if (!collection?.productPageMetadata?.logoTypeUrl)
    missingRequirements.push("Logotype");
  if (!collection?.logoUrl) missingRequirements.push("Logo symbol");
  if (!collection?.name?.trim()) missingRequirements.push("Name");
  if (!collection?.description?.trim()) missingRequirements.push("Description");
  if (
    !collection?.klydos?.some((k) => (k.klydoAnimation || k.loopUrl) && k.theme)
  )
    missingRequirements.push("At least 1 klydo with animation and theme");

  const isAddToClockBlocked = isDraft && missingRequirements.length > 0;
  const addToClockDisabledReason = isAddToClockBlocked
    ? `Draft is missing: ${missingRequirements.join(", ")}`
    : "";

  const handleSaveEdit = useCallback(() => {
    // Send only the fields that actually changed, so a labels-only edit isn't
    // blocked by (or forced to resend) an unrelated empty description, and an
    // unchanged tag list never triggers a metafield rewrite/clear.
    const edit: EditApprovedCollectionInput = {};

    const trimmedName = name.trim();
    if (trimmedName !== (collection?.name ?? "")) {
      if (!trimmedName) {
        setEditError("Name cannot be empty.");
        return;
      }
      edit.name = trimmedName;
    }

    const trimmedDescription = description.trim();
    if (trimmedDescription !== (collection?.description ?? "")) {
      if (!trimmedDescription) {
        setEditError("Description cannot be empty.");
        return;
      }
      edit.description = trimmedDescription;
    }

    const trimmedCoverUrl = coverUrl.trim();
    if (trimmedCoverUrl !== (collection?.coverUrl ?? "")) {
      if (!trimmedCoverUrl) {
        setEditError("Cover image cannot be empty.");
        return;
      }
      edit.coverUrl = trimmedCoverUrl;
    }

    const currentLabels = collection?.productPageMetadata?.labels ?? [];
    const labelsChanged =
      labels.length !== currentLabels.length ||
      labels.some((label, i) => label !== currentLabels[i]);
    if (labelsChanged) {
      edit.labels = labels;
    }

    if (splashBackgroundUrl !== (collection?.splashBackgroundUrl ?? "")) {
      edit.splashBackgroundUrl = splashBackgroundUrl;
    }

    if (menuLogoUrl !== (collection?.menuLogoUrl ?? "")) {
      edit.menuLogoUrl = menuLogoUrl;
    }

    setEditError("");
    setEditSaved(false);
    if (Object.keys(edit).length === 0) {
      return;
    }

    editCollection(
      { collectionId, edit },
      {
        onSuccess: () => {
          setEditSaved(true);
          setTimeout(() => setEditSaved(false), 3000);
        },
        onError: () =>
          setEditError("Failed to save changes. Please try again."),
      },
    );
  }, [
    editCollection,
    collectionId,
    name,
    description,
    labels,
    coverUrl,
    splashBackgroundUrl,
    menuLogoUrl,
    collection,
  ]);

  const handleViewPreview = useCallback(() => {
    const url = isPublished
      ? cachedPreviewData?.productUrl
      : cachedPreviewData?.previewUrl;
    if (url) {
      window.open(url, "_blank");
    }
  }, [cachedPreviewData, isPublished]);

  useEffect(() => {
    setName(collection?.name || "");
    setDescription(collection?.description || "");
    setCoverUrl(collection?.coverUrl || "");
    setLogoUrl(collection?.logoUrl || "");
    setLogoTypeUrl(collection?.productPageMetadata?.logoTypeUrl || "");
    setThumbnailBackgroundUrl(
      collection?.productPageMetadata?.thumbnailBackgroundUrl || "",
    );
    setSplashBackgroundUrl(collection?.splashBackgroundUrl || "");
    setMenuLogoUrl(collection?.menuLogoUrl || "");
    setBadgeColorHex(
      collection?.productPageMetadata?.badgeColorHex || "#000000",
    );
    setKlydosIds(collection?.klydoIds || []);
    setHoldPendulumLogo(collection?.holdPendulumLogo || false);
    setLabels(collection?.productPageMetadata?.labels || []);
    setPlanId(collection?.paymentDetails?.planId);
  }, [collection]);
  if (isLoading) return <div>Loading...</div>;

  const isCollectionLocked =
    collection?.marketplace?.status === MarketplaceStatus.True ||
    isAwaitingScheduledPublish(collection?.marketplace);

  const isPlanChangeLocked = !!collection?.paymentDetails?.isAdvancedActive;

  const hasShopifyProduct = !!collection?.productPageMetadata?.shopifyProductId;
  const hasExistingEdition =
    !!collection?.productPageMetadata?.affiliateBundleShopifyProductId;
  // A draft cannot get an edition (TK-430). The edition copies the cover,
  // thumbnail background and logotype urls into Shopify metafields that the
  // edition page renders directly, and nothing ever rewrites them. On a draft
  // those urls are still in the Cloudinary `*-temp` folders, and approving the
  // collection renames the assets — which would leave the edition page with
  // broken images for good. The API rejects this too; see
  // AffiliateBundleService.getOrCreateAffiliateBundle.
  const createEditionDisabledReason = !isApproved
    ? "Approve the collection first — an edition built from a draft would lose its images."
    : !hasShopifyProduct
      ? "Publish to Shopify first — the edition bundles the collection product."
      : "";
  const isCreateEditionDisabled =
    !!createEditionDisabledReason || isEditionPending;

  const previewReadiness = getPreviewReadiness(collection);
  const isShopifyLinkUnavailable = isPublished
    ? !cachedPreviewData?.productUrl
    : isCollectionLocked || !cachedPreviewData?.previewUrl;
  const isViewShopifyDisabled = isLoadingPreview || isShopifyLinkUnavailable;
  const viewShopifyDisabledReason = isLoadingPreview
    ? ""
    : isPublished && !cachedPreviewData?.productUrl
      ? "No public Shopify page is available for this collection yet."
      : !isPublished && isCollectionLocked
        ? "Preview is unavailable while the collection is pending."
        : !isPublished && !previewReadiness.isReady
          ? previewReadiness.reason
          : !isPublished && !cachedPreviewData?.previewUrl
            ? "Shopify preview is not available yet."
            : "";

  return (
    <>
      <DSDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DSDialogContent>Are you sure?</DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={() => setIsDeleteDialogOpen(false)}>
            No
          </DSSecondaryButton>
          <DSPrimaryButton
            onClick={() => {
              deleteCollection(collectionId);
              onClose();
            }}
          >
            Yes
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>
      <DSDialog
        open={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
      >
        <DSDialogContent>
          <DSTypography variant="h6" sx={{ mb: 2 }}>
            Publish to Marketplace
          </DSTypography>
          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <DSBox sx={{ display: "flex", gap: 2 }}>
              <DSButton
                variant={publishImmediate ? "contained" : "outlined"}
                onClick={() => setPublishImmediate(true)}
              >
                Immediate
              </DSButton>
              <DSButton
                variant={!publishImmediate ? "contained" : "outlined"}
                onClick={() => setPublishImmediate(false)}
              >
                Schedule Date
              </DSButton>
            </DSBox>
            {!publishImmediate && (
              <DateTimePicker
                selectedDate={publishDate}
                onChange={setPublishDate}
                minDate={new Date()}
                isToday={(d) => d.toDateString() === new Date().toDateString()}
              />
            )}
          </DSBox>
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={() => setIsPublishModalOpen(false)}>
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton onClick={handleConfirmPublish}>
            Confirm
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>
      <DSDialog
        open={isEditionModalOpen}
        onClose={() => setIsEditionModalOpen(false)}
      >
        <DSDialogContent>
          <DSTypography variant="h6" sx={{ mb: 1 }}>
            Create Non-Artist Edition
          </DSTypography>
          <DSTypography variant="body2">
            This will create a Shopify bundle product named "{collection?.name}{" "}
            Edition Klydoclock" (no artist branding, no artist payouts).
            Creation can take a few minutes.
          </DSTypography>
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={() => setIsEditionModalOpen(false)}>
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton onClick={handleConfirmCreateEdition}>
            Confirm
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>
      <DSDialog {...rest} onClose={onClose}>
        <DSBox
          sx={{
            p: 2,
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <DSTypography variant="h6">Collection Details</DSTypography>
          {collection?.marketplace?.status === MarketplaceStatus.True && (
            <DSChip
              label="Published to Marketplace"
              color="success"
              size="small"
            />
          )}
          {collection?.marketplace?.status === MarketplaceStatus.Pending && (
            <DSChip label="Pending Publication" color="warning" size="small" />
          )}
          {collection?.marketplace?.status === MarketplaceStatus.False &&
            collection?.marketplace?.date && (
              <DSChip
                label={scheduledMarketplaceLabel(collection.marketplace.date)}
                color="info"
                size="small"
              />
            )}
        </DSBox>
        <CollectionForm
          collectionId={collectionId}
          logo={logoUrl}
          cover={coverUrl}
          logotype={logoTypeUrl}
          thumbnailBackground={thumbnailBackgroundUrl}
          splashBackground={splashBackgroundUrl}
          menuLogo={menuLogoUrl}
          name={name}
          description={description}
          klydosIds={klydosIds}
          setName={setName}
          setDescription={setDescription}
          setCover={setCoverUrl}
          setLogo={setLogoUrl}
          setLogotype={setLogoTypeUrl}
          setThumbnailBackground={setThumbnailBackgroundUrl}
          setSplashBackground={setSplashBackgroundUrl}
          setMenuLogo={setMenuLogoUrl}
          badgeColorHex={badgeColorHex}
          setBadgeColorHex={setBadgeColorHex}
          holdPendulumLogo={holdPendulumLogo}
          setHoldPendulumLogo={setHoldPendulumLogo}
          setKlydosIds={setKlydosIds}
          onUserSelect={(user) => setUserCreatorId(user)}
          creatorId={userCreatorId || ""}
          creator={collection?.creator}
          klydos={collection?.klydos || []}
          labels={labels}
          setLabels={setLabels}
          productPageMetadata={collection?.productPageMetadata}
          disabled
          textEditable={isApproved}
          coverEditable={isApproved}
          onCoverUploadingChange={setIsCoverUploading}
        />
        <DSBox
          sx={{
            padding: 2,
            display: "flex",
            justifyContent: "flex-start",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <DSButton
            style={{
              marginBottom: "15px",
              color: shareSuccess ? "white" : "black",
              backgroundColor: shareSuccess ? "green" : "gray",
            }}
            onClick={handleShareClick}
          >
            {shareLoading
              ? "Sharing..."
              : shareSuccess
                ? "Copied!"
                : "Copy Share Link"}
          </DSButton>
          <DSButton
            style={{
              marginBottom: "15px",
              color: "black",
              backgroundColor: "lightgray",
            }}
            onClick={handleDownloadCover}
            disabled={!coverUrl || isDownloading}
          >
            {isDownloading ? "Downloading..." : "Download Cover"}
          </DSButton>
          <DSButton
            style={{
              marginBottom: "15px",
              color: "black",
              backgroundColor: "lightgray",
            }}
            onClick={handleDownloadFrames}
            disabled={!collection?.klydos?.length || isDownloadingFrames}
          >
            {isDownloadingFrames ? "Downloading..." : "Download Frames"}
          </DSButton>
          <DSTooltip
            title={
              !planId ? "Collection must have a plan before publishing" : ""
            }
            placement="top"
          >
            <span style={{ display: "inline-flex" }}>
              <DSButton
                style={{
                  marginBottom: "15px",
                  color: "white",
                  backgroundColor:
                    isCollectionLocked || !planId ? "#ccc" : "#1976d2",
                }}
                onClick={handlePublishToShopify}
                disabled={isCollectionLocked || isPublishing || !planId}
              >
                {isPublishing ? "Publishing..." : "Publish to Shopify"}
              </DSButton>
            </span>
          </DSTooltip>
          <DSTooltip title={viewShopifyDisabledReason} placement="top">
            <span style={{ display: "inline-flex" }}>
              <DSButton
                style={{
                  marginBottom: "15px",
                  color: "black",
                  backgroundColor: isViewShopifyDisabled ? "#ccc" : "lightgray",
                }}
                onClick={handleViewPreview}
                disabled={isViewShopifyDisabled}
              >
                {isLoadingPreview
                  ? "Loading..."
                  : isPublished
                    ? "Go to Shopify Page"
                    : "View Shopify Preview"}
              </DSButton>
            </span>
          </DSTooltip>
          {hasExistingEdition || editionUrl ? (
            <DSButton
              style={{
                marginBottom: "15px",
                color: "white",
                backgroundColor: editionCopied ? "green" : "#1976d2",
              }}
              onClick={handleCopyEditionUrl}
              disabled={isEditionPending}
            >
              {isEditionPending
                ? "Fetching URL..."
                : editionCopied
                  ? "Copied!"
                  : "Copy Edition URL"}
            </DSButton>
          ) : (
            <DSTooltip title={createEditionDisabledReason} placement="top">
              <span style={{ display: "inline-flex" }}>
                <DSButton
                  style={{
                    marginBottom: "15px",
                    color: "white",
                    backgroundColor: isCreateEditionDisabled
                      ? "#ccc"
                      : "#1976d2",
                  }}
                  onClick={() => setIsEditionModalOpen(true)}
                  disabled={isCreateEditionDisabled}
                >
                  {isEditionPending
                    ? "Creating Edition..."
                    : "Create Non-Artist Edition"}
                </DSButton>
              </span>
            </DSTooltip>
          )}
          <DSTooltip title={addToClockDisabledReason} placement="top">
            <span style={{ display: "inline-flex" }}>
              <DSButton
                style={{
                  marginBottom: "15px",
                  color: "white",
                  backgroundColor: isAddToClockBlocked
                    ? "#ccc"
                    : showAddToClockSection
                      ? "#6c757d"
                      : "#007bff",
                }}
                onClick={() => setShowAddToClockSection((prev) => !prev)}
                disabled={isAddToClockBlocked}
              >
                {showAddToClockSection ? "Hide Add to Clock" : "Add to Clock"}
              </DSButton>
            </span>
          </DSTooltip>
        </DSBox>
        {editionError && (
          <DSBox sx={{ px: 3, pb: 1 }}>
            <DSTypography variant="body2" color="error">
              {editionError}
            </DSTypography>
          </DSBox>
        )}
        {showAddToClockSection && (
          <DSBox sx={{ px: 3, pb: 2 }}>
            <DSTypography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: "bold" }}
            >
              Add Collection to Clock
            </DSTypography>
            <DSBox sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
              <Autocomplete
                sx={{ flexGrow: 1 }}
                options={availableClocks || []}
                filterOptions={noClientFilter}
                getOptionLabel={(option) =>
                  `${option.idf || option.id} (${option.id})`
                }
                value={selectedClock}
                onChange={(_, newValue) => setSelectedClock(newValue)}
                inputValue={clockSearchQuery}
                onInputChange={(_, newInputValue) =>
                  setClockSearchQuery(newInputValue)
                }
                loading={isLoadingClocks || isFetchingNextClocks}
                ListboxProps={listboxProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search for clocks..."
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingClocks && <DSCircularProgress size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <DSBox>
                      <DSTypography variant="body2" sx={{ fontWeight: "bold" }}>
                        {option.idf || option.id}
                      </DSTypography>
                      <DSTypography variant="caption" color="text.secondary">
                        ID: {option.id}
                        {option.registerProduct && " • Registered"}
                      </DSTypography>
                    </DSBox>
                  </li>
                )}
                noOptionsText={
                  clockSearchQuery
                    ? "No clocks found"
                    : "Type to search for clocks"
                }
              />
              <DSButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() =>
                  selectedClock && setShowAddToClockConfirmation(true)
                }
                disabled={!selectedClock}
                sx={{
                  backgroundColor: "#007bff",
                  "&:hover": { backgroundColor: "#0056b3" },
                }}
              >
                Add
              </DSButton>
            </DSBox>
          </DSBox>
        )}
        {/* Sponsored collection (TK-634) — given away free via a generated
            multi-use code instead of being sold; mutually exclusive with the
            normal paid/plan flow. */}
        {isApproved && (
          <DSBox sx={{ px: 3, pb: 2 }}>
            <DSTypography variant="caption" color="text.secondary">
              Sponsored Collection
            </DSTypography>
            <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DSSwitch
                checked={!!collection?.sponsored}
                disabled={isUpdatingSponsored}
                onChange={(_e, checked) =>
                  setSponsored({ collectionId, sponsored: checked })
                }
              />
              <DSTypography variant="body2" color="text.secondary">
                Free, code-gated — not sold as a normal collection.
              </DSTypography>
            </DSBox>
            {sponsoredCodeInfo && (
              <DSTypography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Code: <strong>{sponsoredCodeInfo.code}</strong> · Downloads:{" "}
                <strong>{sponsoredCodeInfo.redemptionCount}</strong>
                {!collection?.sponsored && " (inactive — for payout reference)"}
              </DSTypography>
            )}
            {sponsoredCodeInfo && collection?.sponsored && (
              <SponsoredCodeEditor
                collectionId={collectionId}
                currentCode={sponsoredCodeInfo.code}
                disabled={isUpdatingSponsored}
              />
            )}
          </DSBox>
        )}
        {/* Plan selector */}
        <DSBox sx={{ px: 3, pb: 2 }}>
          <DSTypography variant="caption" color="text.secondary">
            Monetization Plan
          </DSTypography>
          {collection?.sponsored && (
            <DSTypography
              variant="body2"
              color="warning.main"
              sx={{ mb: 1, mt: 0.5 }}
            >
              Collection is sponsored — plan cannot be changed.
            </DSTypography>
          )}
          {collection?.paymentDetails?.isAdvancedActive && (
            <DSTypography
              variant="body2"
              color="warning.main"
              sx={{ mb: 1, mt: 0.5 }}
            >
              Collection is in advanced payment mode — plan cannot be changed.
            </DSTypography>
          )}
          <DSSelect
            fullWidth
            size="small"
            value={planId ?? ""}
            onChange={(e) => {
              const newPlanId = e.target.value;
              if (newPlanId && newPlanId !== planId) {
                const selected = availablePlans.find((p) => p.id === newPlanId);
                if (selected?.isAdvanced) {
                  setPlanError(
                    "Cannot assign an upfront plan to an approved/marketplace collection.",
                  );
                  return;
                }
                setPlanError("");
                setPlanId(newPlanId);
                updatePlan({ collectionId, planId: newPlanId });
              }
            }}
            displayEmpty
            disabled={
              isPlanChangeLocked || isUpdatingPlan || collection?.sponsored
            }
          >
            {availablePlans.map((plan) => (
              <DSMenuItem key={plan.id} value={plan.id}>
                {plan.title ?? plan.id} — ${plan.price} ·{" "}
                {plan.upfrontAmount > 0
                  ? `$${plan.upfrontAmount} upfront`
                  : "No upfront"}{" "}
                ·{" "}
                {plan.revSharePercentage > 0
                  ? `${plan.revSharePercentage}% rev share`
                  : "No rev share"}
                {plan.isAdvanced ? " (Advanced)" : ""}
                {plan.compareAtPrice != null
                  ? ` (was $${plan.compareAtPrice})`
                  : ""}
              </DSMenuItem>
            ))}
          </DSSelect>
          {planError && (
            <DSTypography variant="body2" color="error" sx={{ mt: 0.5 }}>
              {planError}
            </DSTypography>
          )}
          {planId &&
            (() => {
              const selectedPlan = availablePlans.find((p) => p.id === planId);
              if (!selectedPlan) return null;
              return (
                <DSBox sx={{ mt: 1, display: "flex", gap: 2 }}>
                  <DSTypography variant="body2" color="text.secondary">
                    Price: <strong>${selectedPlan.price}</strong>
                    {selectedPlan.compareAtPrice != null && (
                      <>
                        {" "}
                        <s>${selectedPlan.compareAtPrice}</s>
                      </>
                    )}
                  </DSTypography>
                  <DSTypography variant="body2" color="text.secondary">
                    Rev share:{" "}
                    <strong>
                      {selectedPlan.revSharePercentage > 0
                        ? `${selectedPlan.revSharePercentage}%`
                        : "None"}
                    </strong>
                  </DSTypography>
                  {selectedPlan.upfrontAmount > 0 && (
                    <DSTypography variant="body2" color="text.secondary">
                      Upfront: <strong>${selectedPlan.upfrontAmount}</strong>
                    </DSTypography>
                  )}
                </DSBox>
              );
            })()}
        </DSBox>

        <DSDialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <DSButton
            onClick={() => setIsDeleteDialogOpen(true)}
            color="error"
            variant="contained"
          >
            Delete
          </DSButton>
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {editError && (
              <DSTypography variant="body2" color="error">
                {editError}
              </DSTypography>
            )}
            {isApproved && (
              <>
                {editSaved && !isSavingEdit && !editError && (
                  <DSTypography variant="body2" color="success.main">
                    Saved
                  </DSTypography>
                )}
                <DSPrimaryButton
                  onClick={handleSaveEdit}
                  disabled={isSavingEdit || isCoverUploading}
                >
                  {isSavingEdit ? "Saving..." : "Save Details"}
                </DSPrimaryButton>
              </>
            )}
            <DSSecondaryButton onClick={() => onClose?.()}>
              close
            </DSSecondaryButton>
          </DSBox>
        </DSDialogActions>
      </DSDialog>
      <ConfirmationModal
        open={showAddToClockConfirmation}
        onClose={handleCancelAddToClock}
        onConfirm={handleConfirmAddToClock}
        message={`Are you sure you want to add the collection "${collection?.name}" to clock ${selectedClock?.idf || selectedClock?.id}?`}
      />
    </>
  );
};
export default CollectionDialog;
